/**
 * Query-fed Item Pools — the data side (Large Sets PRD §4.3, migration 723).
 *
 * Every membership write here rides the existing pool path
 * (itemPoolsStore.addPrioritiesToList / removePriorityFromList → priority_ids
 * + reconcileItemPoolMemberships), never a new one, and only after a person
 * accepted the diff poolFeedCore produced. The one exception is a LOCKED pool,
 * which changes only through the amend_locked_pool RPC — the server holds the
 * opt-in, the authority bar, and the governance record.
 *
 * pool_arrivals is a bay, not a list: nothing in it is membership truth.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import { getCurrentWorkspaceId } from './supabaseSync';
import { useItemPoolsStore } from '../stores/prioritization/itemPoolsStore';
import { useSavedReportsStore } from '../stores/prioritization/savedReportsStore';
import type { ItemPool } from '../types/prioritization/lifecycle';
import type { QueryNode } from './pql/types';
import { feedSelectionReason, type ArrivalDisposition, type ArrivalRecord } from './poolFeedCore';

// ── Feed binding ─────────────────────────────────────────────────────────────

/**
 * Bind a saved query to a pool. Creates the saved_reports row (the feed's
 * durable truth), waits for it to land, then points item_pools.feed_query_id
 * at it through the store's ordinary update path. Refuses a locked pool: a
 * feed attached after lock would be severed on arrival and only mislead.
 * Either both rows exist or neither does — a half-bound feed is never left
 * behind. `sourceReportId` records the saved query this feed was copied from,
 * when the person was working from one (migration 725) — lineage, not a
 * binding: the feed's own copy is what it evaluates.
 */
export async function bindPoolFeed(
  pool: ItemPool,
  queryText: string,
  queryAst: QueryNode | null,
  sourceReportId?: string | null,
): Promise<{ queryId: string | null; error: string | null }> {
  if (pool.poolLockedAt) {
    return { queryId: null, error: 'This pool is locked. Unlock it to attach a feed, or carry arrivals into its successor.' };
  }
  // Awaited, not fire-and-forget: item_pools.feed_query_id is a FK to
  // saved_reports, so the feed query has to be committed before the pool can
  // point at it.
  const { report, error } = await useSavedReportsStore.getState().createReportPersisted({
    name: `${pool.name} (feed query)`,
    description: `Feed query for the "${pool.name}" item pool. Refreshing the pool re-evaluates this statement.`,
    filterGroups: [],
    queryText,
    queryAst,
    sourceReportId: sourceReportId ?? null,
    sort: [],
    groupBy: null,
    visibleColumns: [],
    activeChart: 'category',
  });
  if (!report) {
    return { queryId: null, error: error ?? 'The feed query could not be saved.' };
  }

  useItemPoolsStore.getState().updateItemPool(pool.id, { feedQueryId: report.id, feedSeveredAt: null });
  const after = useItemPoolsStore.getState().getItemPoolById(pool.id);
  if (after?.feedQueryId !== report.id) {
    // The pool never took the feed, so the query backs nothing — don't leave it
    // in the library claiming to feed this pool.
    useSavedReportsStore.getState().deleteReport(report.id);
    return { queryId: null, error: 'You do not have permission to edit this pool.' };
  }
  return { queryId: report.id, error: null };
}

/** Detach the feed. The saved query is left in the library; the pool keeps its members. */
export function unbindPoolFeed(poolId: string): { error: string | null } {
  useItemPoolsStore.getState().updateItemPool(poolId, { feedQueryId: null, feedSeveredAt: null });
  const after = useItemPoolsStore.getState().getItemPoolById(poolId);
  return { error: after && after.feedQueryId ? 'You do not have permission to edit this pool.' : null };
}

// ── Refresh (unlocked pools only) ────────────────────────────────────────────

export interface FeedRefreshResult {
  added: string[];
  removed: string[];
  error: string | null;
}

/**
 * Write an accepted diff. `adds` and `removals` are exactly what the person
 * ticked — never the raw diff. Locked pools are refused here; that is what
 * amendLockedPool is for.
 */
export function applyFeedRefresh(params: {
  pool: ItemPool;
  adds: string[];
  removals: string[];
  actorId?: string;
}): FeedRefreshResult {
  const { pool, adds, removals, actorId } = params;
  if (pool.poolLockedAt) {
    return { added: [], removed: [], error: 'This pool is locked; its membership can only change through an amendment.' };
  }
  if (!pool.feedQueryId) {
    return { added: [], removed: [], error: 'This pool has no feed.' };
  }
  const store = useItemPoolsStore.getState();
  const now = new Date();
  // The rule as it stands right now — stamped into each membership, because the
  // feed's rule can be edited later and the id alone would then point at a rule
  // these items never matched.
  const ruleText = useSavedReportsStore.getState().getReportById(pool.feedQueryId)?.queryText;
  let added: string[] = [];
  if (adds.length > 0) {
    const res = store.addPrioritiesToList(
      pool.id,
      adds,
      pool.cycleId,
      actorId,
      feedSelectionReason(pool.feedQueryId, now, ruleText),
      'filter',
    );
    if (res.error) return { added: [], removed: [], error: res.error };
    added = res.added;
  }
  const removed: string[] = [];
  for (const id of removals) {
    store.removePriorityFromList(pool.id, id, pool.cycleId, actorId);
    removed.push(id);
  }
  store.updateItemPool(pool.id, { feedLastRefreshedAt: now });
  return { added, removed, error: null };
}

// ── Arrival Bay ──────────────────────────────────────────────────────────────

function toArrival(r: Record<string, unknown>): ArrivalRecord {
  return {
    itemId: String(r.item_id),
    disposition: r.disposition as ArrivalDisposition,
    matchedAt: String(r.matched_at),
    carriedToPoolId: (r.carried_to_pool_id as string | null) ?? null,
  };
}

export async function fetchArrivals(poolId: string): Promise<{ data: ArrivalRecord[]; error: string | null }> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('pool_arrivals')
    .select('item_id, disposition, matched_at, carried_to_pool_id')
    .eq('pool_id', poolId)
    .order('matched_at', { ascending: true });
  if (error) return { data: [], error: error.message };
  return { data: (data ?? []).map((r) => toArrival(r as unknown as Record<string, unknown>)), error: null };
}

/** Record new sightings as 'held'. Idempotent per (pool, item). */
export async function recordArrivals(pool: ItemPool, itemIds: string[]): Promise<{ error: string | null }> {
  if (itemIds.length === 0) return { error: null };
  const workspaceId = pool.workspaceId ?? getCurrentWorkspaceId();
  if (!workspaceId) return { error: 'No workspace.' };
  const db = getTypedSupabaseClient();
  const { error } = await db
    .from('pool_arrivals')
    .upsert(
      itemIds.map((itemId) => ({ workspace_id: workspaceId, pool_id: pool.id, item_id: itemId, disposition: 'held' })),
      { onConflict: 'pool_id,item_id', ignoreDuplicates: true },
    );
  return { error: error ? error.message : null };
}

/** Dispose a held arrival by hand: dismiss it, or carry it into a successor pool. */
export async function disposeArrival(params: {
  poolId: string;
  itemId: string;
  disposition: Extract<ArrivalDisposition, 'dismissed' | 'carried'>;
  actorId?: string;
  carriedToPoolId?: string;
}): Promise<{ error: string | null }> {
  const { poolId, itemId, disposition, actorId, carriedToPoolId } = params;
  if (disposition === 'carried' && !carriedToPoolId) return { error: 'Choose the pool to carry into.' };
  const db = getTypedSupabaseClient();
  const { error } = await db
    .from('pool_arrivals')
    .update({
      disposition,
      disposed_at: new Date().toISOString(),
      disposed_by: actorId ?? null,
      carried_to_pool_id: disposition === 'carried' ? carriedToPoolId : null,
    })
    .eq('pool_id', poolId)
    .eq('item_id', itemId);
  return { error: error ? error.message : null };
}

/**
 * Carry a held arrival into a successor pool: an ordinary add on the
 * successor (it is open, so the ordinary path applies) plus the disposition.
 */
export async function carryArrival(params: {
  fromPool: ItemPool;
  toPool: ItemPool;
  itemId: string;
  actorId?: string;
}): Promise<{ error: string | null }> {
  const { fromPool, toPool, itemId, actorId } = params;
  if (toPool.poolLockedAt) return { error: `"${toPool.name}" is locked too — carry into an open pool.` };
  const res = useItemPoolsStore
    .getState()
    .addPrioritiesToList(toPool.id, [itemId], toPool.cycleId, actorId, `carried from ${fromPool.name} (arrival bay)`, 'filter');
  if (res.error) return { error: res.error };
  return disposeArrival({ poolId: fromPool.id, itemId, disposition: 'carried', actorId, carriedToPoolId: toPool.id });
}

// ── Amendment (locked pools; governed) ───────────────────────────────────────

export interface AmendResult {
  added: string[];
  sessionsFlagged: number;
  error: string | null;
}

/** The only path that changes a locked pool. Everything that matters is checked on the server. */
export async function amendLockedPool(poolId: string, itemIds: string[], reason: string): Promise<AmendResult> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('amend_locked_pool', {
    p_pool_id: poolId,
    p_item_ids: itemIds,
    p_reason: reason,
  });
  if (error) return { added: [], sessionsFlagged: 0, error: error.message };
  const res = data as { success: boolean; error?: string; added?: string[]; sessions_flagged?: number };
  if (!res?.success) return { added: [], sessionsFlagged: 0, error: res?.error ?? 'Amendment refused.' };
  const added = res.added ?? [];
  if (added.length > 0) {
    // Mirror the server's append into the store so the ranked list shows it now.
    const pool = useItemPoolsStore.getState().getItemPoolById(poolId);
    if (pool) {
      useItemPoolsStore.setState((s) => ({
        itemPools: s.itemPools.map((p) =>
          p.id === poolId ? { ...p, priorityIds: [...p.priorityIds, ...added.filter((id) => !p.priorityIds.includes(id))] } : p,
        ),
      }));
    }
  }
  return { added, sessionsFlagged: res.sessions_flagged ?? 0, error: null };
}
